"use client";

import { useState } from "react";
import { cn } from "@/app/lib/utils/cn";
import CountUp from "@/app/ui/atomic/animations/CountUp";

type TabId = "investor" | "entrepreneur" | "worker" | "business-owner";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  color: string;
  stats: { 
    value: number; 
    prefix?: string; 
    suffix?: string; 
    label: string; 
    decimals?: number 
  }[];
}

const tabs: Tab[] = [
  {
    id: "investor",
    label: "Investors",
    color: "gold",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
      </svg>
    ),
    stats: [
      { value: 1.3, prefix: "$", suffix: "B", label: "raised via Reg CF since 2016", decimals: 1 },
      { value: 69, suffix: "%", label: "of campaigns hit funding goals" },
      { value: 257, prefix: "$", suffix: "K", label: "average successful raise" },
    ],
  },
  {
    id: "entrepreneur",
    label: "Entrepreneurs",
    color: "pink",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
      </svg>
    ),
    stats: [
      { value: 80, suffix: "%", label: "5-year co-op survival rate" },
      { value: 751, suffix: "+", label: "US worker co-ops (3x growth in a decade)" },
      { value: 480, prefix: "$", suffix: "M+", label: "annual co-op sector revenue" },
    ],
  },
  {
    id: "worker",
    label: "Workers",
    color: "purple",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>
    ),
    stats: [
      { value: 2, suffix: ":1", label: "typical co-op pay ratio (vs 300:1)" },
      { value: 52, suffix: "%", label: "of co-op workers are women" },
      { value: 47, suffix: "%", label: "are people of color" },
    ],
  },
  {
    id: "business-owner",
    label: "Business Owners",
    color: "gold",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
      </svg>
    ),
    stats: [
      { value: 10, suffix: "M+", label: "businesses changing hands this decade" },
      { value: 33, suffix: "%", label: "have a succession plan" },
      { value: 10, suffix: "K", label: "boomers turn 65 daily" },
    ],
  },
];

const colorClasses: Record<string, { 
  border: string; 
  bg: string; 
  text: string; 
  activeBg: string 
}> = {
  gold: {
    border: "border-(--gold-accent)",
    bg: "bg-(--gold)/10",
    text: "text-(--gold-text)",
    activeBg: "bg-(--gold)",
  },
  pink: {
    border: "border-(--brand)",
    bg: "bg-(--brand)/10",
    text: "text-(--brand)",
    activeBg: "bg-(--brand)",
  },
  purple: {
    border: "border-(--purple-accent)",
    bg: "bg-(--purple)/10",
    text: "text-(--purple-accent)",
    activeBg: "bg-(--purple)",
  },
};

function CoopStat({ 
  value, 
  prefix = "", 
  suffix = "", 
  label,
  decimals = 0
}: { 
  value: number; 
  prefix?: string; 
  suffix?: string; 
  label: string;
  decimals?: number;
}) {
  return (
    <div>
      <span className="font-['Playfair_Display'] font-semibold text-(--brand) text-2xl">
        <CountUp end={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </span>
      <p className="text-sm text-(--text-muted) mt-1">{label}</p>
    </div>
  );
}

export default function StatsTabs() {
  const [activeTab, setActiveTab] = useState<TabId>("investor");

  const currentTab = tabs.find((t) => t.id === activeTab)!;
  const colors = colorClasses[currentTab.color]!;

  return (
    <div className="theme-card-alt rounded-xl overflow-hidden shadow-sm">
      <div className="flex border-b border-(--border-color)" role="tablist" aria-label="Statistics by audience">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const tabColors = colorClasses[tab.color]!;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-3 py-4 text-sm font-['Font-Bold'] transition-all cursor-pointer",
                isActive
                  ? tabColors.activeBg
                  : "text-(--text-muted) hover:bg-(--purple)/5"
              )}
              style={
                isActive 
                  ? { 
                      color: tab.color === "gold" 
                        ? "var(--text-on-gold)" 
                        : tab.color === "purple" 
                        ? "var(--gold)" 
                        : "var(--text-on-pink)" 
                    } 
                  : undefined
              }
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              role="tab"
              tabIndex={isActive ? 0 : -1}
            >
              <span className={cn("hidden sm:block", isActive && "opacity-90")}>
                {tab.icon}
              </span>
              <span className="text-xs sm:text-sm">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div 
        className={cn("p-6 border-t-4", colors.border)}
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        <div className="grid sm:grid-cols-3 gap-4">
          {currentTab.stats.map((stat, index) => (
            <div
              key={index}
              className={cn("text-center p-4 rounded-lg", colors.bg)}
            >
              <CoopStat
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
                label={stat.label}
                decimals={stat.decimals}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
